import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session keep-alive interval (5 minutes)
const KEEP_ALIVE_INTERVAL = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));

  // Keep session alive while user is active
  useEffect(() => {
    if (!token) return;
    
    let keepAliveTimer = null;
    let lastActivity = Date.now();
    
    // Track user activity
    const updateActivity = () => {
      lastActivity = Date.now();
    };
    
    // Keep-alive ping - only if user was recently active
    const keepAlive = async () => {
      // Only ping if user was active in last 30 minutes
      if (Date.now() - lastActivity < 30 * 60 * 1000) {
        try {
          await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          // Try to refresh token if ping fails
          if (refreshToken) {
            try {
              await refreshAccessToken();
            } catch (refreshError) {
              console.error('Session expired');
            }
          }
        }
      }
    };
    
    // Listen for user activity
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);
    
    // Start keep-alive timer
    keepAliveTimer = setInterval(keepAlive, KEEP_ALIVE_INTERVAL);
    
    return () => {
      clearInterval(keepAliveTimer);
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, [token, refreshToken]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Try refreshing token if we have one
      if (refreshToken) {
        try {
          await refreshAccessToken();
        } catch (refreshError) {
          logout();
        }
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login?remember_me=${rememberMe}`, { username, password });
    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    if (rememberMe && refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
      setRefreshToken(refresh_token);
    }
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const refreshAccessToken = async () => {
    const response = await axios.post(`${API}/auth/refresh-token`, {
      refresh_token: refreshToken
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return access_token;
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/auth/me`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(response.data);
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post(`${API}/auth/change-password`, 
      { current_password: currentPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` }}
    );
  };

  const requestPasswordReset = async (email) => {
    await axios.post(`${API}/auth/request-password-reset`, { email });
  };

  const resetPassword = async (token, newPassword) => {
    await axios.post(`${API}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
  };

  const getClientBookings = async () => {
    const response = await axios.get(`${API}/client/bookings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const cancelBooking = async (bookingId) => {
    await axios.post(`${API}/client/bookings/${bookingId}/cancel`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const loginPartner = async (username, password) => {
    const response = await axios.post(`${API}/partners/login`, { username, password });
    const { access_token, user: partnerData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(partnerData);
    return partnerData;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      loading, 
      token, 
      login, 
      loginPartner,
      register, 
      logout, 
      updateProfile, 
      changePassword,
      requestPasswordReset,
      resetPassword,
      getClientBookings,
      cancelBooking,
      refreshAccessToken,
      isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
      isStaff: user?.role === 'staff',
      isAdminOrStaff: user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'staff',
      isClient: user?.role === 'client',
      isPartner: user?.role === 'partner'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
